import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Settings2, X } from 'lucide-react';
import { Button, EmptyState, Field, Select, Spinner } from '@zt/ui';
import { api, ApiError } from '../../lib/api';
import { HelpTip } from '../../lib/help';
import { useT } from '../../lib/i18n';
import { useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';
import type { SavedReportRun, WidgetList } from '../../lib/types-faz8';
import { Table, td, tdR, th, thR } from './blocks';
import { ColumnChart } from './charts';
import { useDimLabel, useMetricFormat } from './custom';

/**
 * Ana panel rapor bileşenleri (F8-09, ADR-809): kullanıcının eklediği kayıtlı raporlar, sırasıyla. Her bileşen raporu
 * görüntüleyenin yetkileriyle çalıştırır. Sıralama klavyeyle de yapılabilsin diye sürükle-bırak yerine yukarı/aşağı
 * düğmeleri kullanılır.
 */
export function DashboardWidgets() {
  const t = useT();
  const { can } = usePermissions();
  const allowed = can('reports.custom.read');
  const q = useQuery({
    queryKey: ['dashboard-widgets'],
    queryFn: () => api.get<WidgetList>('/v1/admin/dashboard/widgets'),
    enabled: allowed,
  });
  const reorder = useApiMutation((ids: string[]) => api.put('/v1/admin/dashboard/widgets/order', { ids }), {
    invalidate: [['dashboard-widgets']],
  });
  if (!allowed) return null;
  const list = q.data ?? [];
  const move = (i: number, d: -1 | 1) => {
    const ids = list.map((w) => w.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };
  return (
    <section aria-labelledby="report-widgets-title" className="mt-10">
      <h2 id="report-widgets-title" className="mb-3 flex items-center gap-1 text-lg font-semibold text-ink">
        {t('admin.reportWidgets.title')}
        <HelpTip k="dashboard.widgets" />
      </h2>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.length ? (
        <EmptyState
          title={t('admin.reportWidgets.empty')}
          action={
            <Link to="/rapor-olusturucu" className="font-medium text-pine hover:underline">
              {t('admin.reportWidgets.goBuilder')}
            </Link>
          }
        />
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2">
          {list.map((w, i) => (
            <li key={w.id} className={w.size === 'full' ? 'lg:col-span-2' : undefined}>
              <Widget
                widget={w}
                first={i === 0}
                last={i === list.length - 1}
                onUp={() => move(i, -1)}
                onDown={() => move(i, 1)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Widget({
  widget,
  first,
  last,
  onUp,
  onDown,
}: {
  widget: WidgetList[number];
  first: boolean;
  last: boolean;
  onUp: () => void;
  onDown: () => void;
}) {
  const t = useT();
  const [settings, setSettings] = useState(false);
  const name = widget.name ?? t('admin.reportWidgets.unavailableTitle');
  const run = useQuery({
    queryKey: ['custom-report', 'saved', widget.savedReportId],
    queryFn: () => api.get<SavedReportRun>(`/v1/admin/reports/custom/${widget.savedReportId}/run`),
    enabled: widget.available,
    retry: false,
  });
  const update = useApiMutation(
    (body: { display?: string; size?: string }) =>
      api.patch(`/v1/admin/dashboard/widgets/${widget.id}`, body),
    { invalidate: [['dashboard-widgets']] },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/dashboard/widgets/${widget.id}`), {
    invalidate: [['dashboard-widgets']],
    success: t('admin.reportWidgets.removed'),
  });
  return (
    <article className="h-full rounded-md border border-line bg-surface p-4" aria-label={name}>
      <header className="mb-3 flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-ink">
          {widget.available ? (
            <Link
              to="/rapor-olusturucu"
              search={{ rapor: widget.savedReportId }}
              className="text-ink hover:text-pine hover:underline"
            >
              {name}
            </Link>
          ) : (
            name
          )}
        </h3>
        <span className="inline-flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={first}
            aria-label={t('admin.reportWidgets.moveUp', { name })}
            onClick={onUp}
          >
            <ArrowUp className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={last}
            aria-label={t('admin.reportWidgets.moveDown', { name })}
            onClick={onDown}
          >
            <ArrowDown className="h-4 w-4" aria-hidden />
          </Button>
          {widget.available && (
            <Button
              size="sm"
              variant="ghost"
              aria-expanded={settings}
              aria-label={t('admin.reportWidgets.settings', { name })}
              onClick={() => setSettings((s) => !s)}
            >
              <Settings2 className="h-4 w-4" aria-hidden />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('admin.reportWidgets.remove', { name })}
            loading={remove.isPending}
            onClick={() => remove.mutate(undefined)}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </span>
      </header>
      {settings && (
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Field label={t('admin.reportWidgets.display')}>
            {(p) => (
              <Select
                {...p}
                value={widget.display}
                onChange={(e) => update.mutate({ display: e.target.value })}
              >
                {(['table', 'chart', 'number'] as const).map((d) => (
                  <option key={d} value={d}>
                    {t(`admin.reportWidgets.displays.${d}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.reportWidgets.size')}>
            {(p) => (
              <Select {...p} value={widget.size} onChange={(e) => update.mutate({ size: e.target.value })}>
                {(['half', 'full'] as const).map((d) => (
                  <option key={d} value={d}>
                    {t(`admin.reportWidgets.sizes.${d}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      )}
      {!widget.available ? (
        <p className="text-sm text-ink-3">{t('admin.reportWidgets.unavailable')}</p>
      ) : run.isError ? (
        <p role="alert" className="text-sm text-rose">
          {run.error instanceof ApiError ? run.error.title : String(run.error)}
        </p>
      ) : !run.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <WidgetBody r={run.data.result} display={widget.display} name={name} />
      )}
    </article>
  );
}

const ROWS = 8;

function WidgetBody({ r, display, name }: { r: SavedReportRun['result']; display: string; name: string }) {
  const t = useT();
  const fmt = useMetricFormat();
  const dimLabel = useDimLabel();
  const metricName = (m: string) => t(`admin.reportBuilder.metrics.${m}`);
  const first = r.metrics[0]!;
  if (display === 'number' || (!r.rows.length && !r.column))
    return (
      <dl className="grid grid-cols-2 gap-3">
        {r.metrics.map((m) => (
          <div key={m.key}>
            <dt className="text-sm text-ink-3">{metricName(m.key)}</dt>
            <dd className="text-2xl font-semibold text-ink">{fmt(m.kind, r.totals.values[m.key] ?? null)}</dd>
          </div>
        ))}
      </dl>
    );
  const chartable = !r.column && r.rows.length === 1 && first.kind !== 'bps';
  if (display === 'chart' && chartable && r.data.length)
    return (
      <ColumnChart
        title={`${name}: ${metricName(first.key)}`}
        points={r.data.slice(0, 24).map((row) => ({
          key: row.keys.join('/'),
          label: dimLabel(r.rows[0]!, row.keys[0]!, row.labels[0] ?? null),
          value: row.values[first.key] ?? 0,
        }))}
        formatValue={(n) => fmt(first.kind, n)}
        formatTick={(n) => fmt(first.kind, Math.round(n))}
      />
    );
  const rows = r.data.slice(0, ROWS);
  return (
    <>
      {display === 'chart' && !chartable && (
        <p className="mb-2 text-xs text-ink-3">{t('admin.reportWidgets.chartUnavailable')}</p>
      )}
      <Table
        caption={name}
        head={
          <tr>
            {r.rows.map((d) => (
              <th key={d} scope="col" className={th}>
                {t(`admin.reportBuilder.dims.${d}`)}
              </th>
            ))}
            {(r.column ? [first] : r.metrics).map((m) => (
              <th key={m.key} scope="col" className={thR}>
                {metricName(m.key)}
              </th>
            ))}
          </tr>
        }
      >
        {rows.map((row) => (
          <tr key={row.keys.join('/')} className="border-t border-line">
            {row.keys.map((k, i) =>
              i === 0 ? (
                <th key={i} scope="row" className={`${td} text-start font-normal`}>
                  {dimLabel(r.rows[i]!, k, row.labels[i] ?? null)}
                </th>
              ) : (
                <td key={i} className={td}>
                  {dimLabel(r.rows[i]!, k, row.labels[i] ?? null)}
                </td>
              ),
            )}
            {(r.column ? [first] : r.metrics).map((m) => (
              <td key={m.key} className={tdR}>
                {fmt(m.kind, row.values[m.key] ?? null)}
              </td>
            ))}
          </tr>
        ))}
      </Table>
      {r.totalRows > rows.length && (
        <p className="mt-2 text-xs text-ink-3">
          {t('admin.reportWidgets.moreRows', { count: r.totalRows - rows.length })}
        </p>
      )}
    </>
  );
}
